// topbarFocus.tsx — lets a module shape App.tsx's top bar:
//
// - `useHideTopbar(active)` hides the bar (page title, global search, branch
//   switcher, user menu) while the module shows a full-page detail view with
//   its own identity header, e.g. an open claim (ClaimHeader) or an open IP
//   admission (the Case Sheet's PatientHeader). Same reasoning as the visit
//   workflow's existing suppression in App.tsx: that row is duplicate chrome
//   there, and the vertical space belongs to the record. Desktop/tablet only —
//   App.tsx keeps the bar on phone, where it holds the only menu button.
//
// - `useTopbarContent(node)` replaces the bar's left slot (normally the module
//   title) with the module's own content — the Dashboard puts its greeting and
//   live clock there (docs/design/dashboard, regions 2-3) rather than spending
//   a row of the page on them. Pass a stable element (useMemo); the slot
//   reverts to the module title on unmount.
//
// Both flags clear on unmount, so leaving the module can never strand the app
// without its bar or with another module's content in it.
import { createContext, useContext, useEffect, type ReactNode } from 'react';

type SetHidden = (hidden: boolean) => void;
type SetContent = (content: ReactNode | null) => void;
interface TopbarFocus { setHidden: SetHidden; setContent: SetContent }
const TopbarFocusContext = createContext<TopbarFocus>({ setHidden: () => {}, setContent: () => {} });

export function TopbarFocusProvider({ setHidden, setContent, children }: { setHidden: SetHidden; setContent: SetContent; children: ReactNode }) {
  return <TopbarFocusContext.Provider value={{ setHidden, setContent }}>{children}</TopbarFocusContext.Provider>;
}

export function useHideTopbar(active: boolean) {
  const { setHidden } = useContext(TopbarFocusContext);
  useEffect(() => {
    setHidden(active);
    return () => setHidden(false);
  }, [active, setHidden]);
}

export function useTopbarContent(content: ReactNode | null) {
  const { setContent } = useContext(TopbarFocusContext);
  useEffect(() => {
    setContent(content);
    return () => setContent(null);
  }, [content, setContent]);
}
